import { useContext, useState, useEffect, useMemo, useCallback } from "react";
import Layout from "../../layouts/PayrollLayout";
import { getToken } from "../../utils/tokenUtils";
import { useEmployeeDetails } from "../../contexts/EmployeeDetailsContext";
import { AuthContext } from "../../contexts/AuthContext";
import Modal from "../../components/UI/Modal";
import ReusableTable from "../../components/UI/ReusableTable";
import Notification from "../../components/UI/Notification";
import EmployeeDetailForm from "../../components/Form/EmployeeDetailForm";
import Drawer from "../../components/UI/Drawer";
import ReactPaginate from "react-paginate";
import {
  X,
  MoreVertical,
  Edit,
  User,
  Globe,
  MapPin,
  Info,
  Heart,
  Book,
  FileText,
} from "lucide-react";

export default function EmploymentDetailsManagement() {
  const {
    employeeDetails,
    fetchEmployeeDetails,
    addEmployeeDetails,
    updateEmployeeDetails,
    deleteEmployeeDetails,
    pagination,
    loading,
    error,
    errors: contextFieldErrors,
  } = useEmployeeDetails();
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState("bottom");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const { user } = useContext(AuthContext);
  const [formErrors, setFormErrors] = useState({}); // Validation errors
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [localFieldErrors, setLocalFieldErrors] = useState({});
  const [notification, setNotification] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFiltering, setIsFiltering] = useState(false);
  const [activeSection, setActiveSection] = useState("personal");
  const token = getToken();

  const sections = [
    { id: "personal", label: "Personal Info", icon: <User size={18} /> },
    { id: "demographic", label: "Demographic Info", icon: <Globe size={18} /> },
    { id: "address", label: "Address", icon: <MapPin size={18} /> },
    { id: "physical", label: "Physical Info", icon: <Info size={18} /> },
    { id: "family", label: "Family Info", icon: <Heart size={18} /> },
    { id: "language", label: "Languages", icon: <Book size={18} /> },
    { id: "documents", label: "Documents", icon: <FileText size={18} /> },
  ];

  // Helper component for displaying information
  function InfoItem({ label, value }) {
    return (
      <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
        <p className="text-sm font-medium text-gray-600 mb-1 dark:text-white">
          {label}
        </p>
        <p className="text-gray-800 dark:text-gray-200">{value || "N/A"}</p>
      </div>
    );
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownId && !event.target.closest(".dropdown-container")) {
        setOpenDropdownId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdownId]);

  // Function to determine dropdown position
  const handleDropdownToggle = (userId, event) => {
    if (openDropdownId === userId) {
      setOpenDropdownId(null);
    } else {
      const button = event.currentTarget;
      const rect = button.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 150; // Approximate dropdown height

      // Check if there's enough space below
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        setDropdownPosition("top");
      } else {
        setDropdownPosition("bottom");
      }

      setOpenDropdownId(userId);
    }
  };

  const convertFormDataToObject = (formData) => {
    return Object.fromEntries(formData.entries());
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingRequest(null);
  };

  const openDrawerForDetails = (employee) => {
    setSelectedEmployee(employee);
    setIsDrawerOpen(true);
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedEmployee(null);
  };

  const handleEmployeeDetailSubmit = useCallback(
    async (employeeDetail) => {
      try {
        if (editingRequest) {
          if (employeeDetail instanceof FormData) {
            // If it's FormData, update directly
            const result = await updateEmployeeDetails(employeeDetail);
            if (!result?.success) {
              throw result;
            }
          } else {
            // If it's a regular object, merge with editingRequest
            const result = await updateEmployeeDetails({
              ...editingRequest,
              ...employeeDetail,
            });
            if (!result?.success) {
              throw result;
            }
          }
          setNotification({
            message:
              "Employee detail and emergency contact updated successfully",
            type: "success",
          });
        } else {
          const result = await addEmployeeDetails(employeeDetail);
          if (!result?.success) {
            throw result;
          }
          setNotification({
            message: "Employee Detail added successfully.",
            type: "success",
          });
        }
        closeModal();
      } catch (err) {
        const fieldErrors =
          err?.errors || contextFieldErrors || localFieldErrors || {};
        const generalMessage = err?.message || error || "Something went wrong.";
        if (fieldErrors && Object.keys(fieldErrors).length > 0) {
          const messages = Object.entries(fieldErrors)
            .map(
              ([field, msgs]) =>
                `${Array.isArray(msgs) ? msgs.join(", ") : msgs}`
            )
            .join("\n");
          setFormErrors(fieldErrors);
          setLocalFieldErrors(fieldErrors);
          setNotification({ message: messages, type: "error" });
        } else {
          console.error("Unexpected error during submission:", err);
          setNotification({ message: generalMessage, type: "error" });
        }
      }
    },
    [editingRequest, addEmployeeDetails, updateEmployeeDetails, closeModal]
  );

  const openModalForAdd = () => {
    setEditingRequest(null);
    setIsModalOpen(true);
  };

  const openModalForEdit = (request) => {
    setEditingRequest(request);
    setIsModalOpen(true);
  };

  const excludeFilterColumns = useMemo(
    () => ["actions", "category", "job_title", "employee_id"],
    []
  );

  // Memoized Columns Definition
  const columns = useMemo(
    () => [
      {
        label: "EMP ID",
        field: "employee_id",
        render: (row) => (
          <div
            onClick={() => {
              openDrawerForDetails(row);
              setOpenDropdownId(null);
            }}
            className="text-sm text-blue-500 hover:underline hover:underline-offset-4 cursor-pointer"
          >
            {row.employee_id}
          </div>
        ),
      },
      { label: "Full Name", field: "full_name" },
      { label: "Date of Birth", field: "date_of_birth" },
      { label: "Gender", field: "gender" },
      { label: "Nationality", field: "nationality" },
      {
        label: "Actions",
        field: "actions",
        render: (row) => (
          <div className="flex items-center justify-center">
            <div className="relative dropdown-container">
              <button
                onClick={(e) => handleDropdownToggle(row.id, e)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
              >
                <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              </button>

              {openDropdownId === row.id && (
                <div
                  className={`absolute right-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-[9999] min-w-[160px] ${
                    dropdownPosition === "top"
                      ? "bottom-full mb-1"
                      : "top-full mt-1"
                  }`}
                  style={{
                    maxHeight: "300px",
                    overflowY: "auto",
                  }}
                >
                  <div className="flex flex-col space-y-0">
                    {/* View Details */}
                    <button
                      onClick={() => {
                        openDrawerForDetails(row);
                        setOpenDropdownId(null);
                      }}
                      title="View"
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors block"
                    >
                      View
                    </button>

                    {/* Edit */}
                    <button
                      onClick={() => {
                        openModalForEdit(row);
                        setOpenDropdownId(null);
                      }}
                      title="Edit"
                      className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors block"
                    >
                      Edit
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ),
      },
    ],
    [
      openDropdownId,
      dropdownPosition,
      handleDropdownToggle,
      openDrawerForDetails,
      openModalForEdit,
    ]
  );

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
      fetchEmployeeDetails(newPage);
    }
  };

  const handleDeleteEmployeeDetail = async (id) => {
    try {
      await deleteEmployeeDetails(id);
      setNotification("Employee Detail deleted successfully");
    } catch (error) {
      setNotification("Failed to delete employee detail");
    }
  };

  return (
    <Layout title="Employee Details">
      <div className="bg-white dark:bg-gray-900 p-4 font-poppins rounded-2xl">
        {notification && (
          <Notification
            message={notification.message}
            type={notification.type}
            onClose={() => setNotification(null)}
          />
        )}

        <div className="flex items-center justify-start mb-6">
          <h2 className="text-lg font-medium dark:text-white">Employee List</h2>
        </div>

        <div>
          {/* data table */}
          <ReusableTable
            data={employeeDetails}
            loading={loading}
            columns={columns}
            filterable={false}
          />
          {/* data table */}

          {/* pagination */}
          {!isFiltering && employeeDetails?.length > 0 && (
            // <ReactPaginate
            // breakLabel="..."
            // nextLabel="Next >"
            // onPageChange={(event) => handlePageChange(event.selected + 1)}
            // forcePage={currentPage - 1}
            // pageRangeDisplayed={5}
            // disabledClassName='opacity-50 cursor-not-allowed'
            // disabledLinkClassName='pointer-events-none'
            // pageCount={pagination.totalPages}
            // previousLabel="< Prev"
            // previousClassName='dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-800'
            // nextClassName="dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-800"
            // renderOnZeroPageCount={null}
            // containerClassName="flex items-center justify-center mt-4 mb-1 font-poppins"
            // pageClassName="mx-1 px-3 py-1 border rounded dark:text-white font-poppins"
            // activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
            // />

            <ReactPaginate
              breakLabel="..."
              nextLabel="Next >"
              onPageChange={(event) => handlePageChange(event.selected + 1)}
              forcePage={currentPage - 1}
              pageRangeDisplayed={5}
              disabledClassName="opacity-50 cursor-not-allowed"
              disabledLinkClassName="pointer-events-none"
              pageCount={pagination.totalPages}
              previousLabel="< Prev"
              previousClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
              previousLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
              nextClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
              nextLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
              renderOnZeroPageCount={null}
              containerClassName="flex items-center justify-center mt-4 font-poppins"
              pageClassName="mx-1 border rounded dark:text-white font-poppins"
              pageLinkClassName="block w-full h-full px-3 py-1 cursor-pointer"
              activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
              activeLinkClassName="bg-blue-500 text-white dark:bg-black dark:text-white hover:bg-blue-600 dark:hover:bg-gray-900"
            />
          )}
        </div>

        {/* Modal for Adding or Editing */}
        <Modal
          isOpen={isModalOpen}
          onRequestClose={closeModal}
          title={
            editingRequest ? "Edit Employee Details" : "Add New Employee Detail"
          }
        >
          <EmployeeDetailForm
            closeModal={closeModal}
            onSubmit={handleEmployeeDetailSubmit}
            initialValues={editingRequest || {}}
            isEditing={Boolean(editingRequest)}
            loading={loading}
          />
        </Modal>

        {/* drawer for details view */}
        <Drawer
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          title="Employee Details"
          ChilDiv=""
          ChildParent=""
          height_width="md:max-w-3xl md:max-h-3xl"
          border_radius="md:rounded-md"
          showClose=""
        >
          {/* Drawer Header */}
          <div className="border-b border-gray-200 dark:border-gray-700 p-4 overflow-auto flex justify-between items-center bg-blue-600 text-white">
            <div className="flex items-center gap-4">
              <h2 className="text-lg font-medium">Employee Details</h2>
              <button
                onClick={() => {
                  openModalForEdit(selectedEmployee);
                  setIsDrawerOpen(false);
                }}
                title="Edit"
              >
                <Edit className="w-5 h-5 text-white" />
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsDrawerOpen(false)}
                className="p-1 rounded-full hover:bg-blue-700 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {selectedEmployee ? (
            <div className="flex flex-col">
              {/* Profile Banner */}
              <div className="bg-blue-50 dark:bg-gray-900 p-6 flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center border-2 border-blue-300">
                    {selectedEmployee.profile_image ? (
                      <img
                        src={`https://hrmspayroll.prepstripe.com/storage/${selectedEmployee.profile_image}`}
                        alt="Profile"
                        className="h-full w-full rounded-full object-cover"
                      />
                    ) : (
                      <User size={32} className="text-blue-500" />
                    )}
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                    {selectedEmployee.full_name}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-200">
                    ID: {selectedEmployee.employee_id}
                  </p>
                </div>
              </div>

              {/* Navigation Tabs */}
              <div className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                <div className="flex overflow-x-auto overflow-y-hidden scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 py-2 px-4 ">
                  {sections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`flex items-center px-3 py-2 text-sm rounded-md mr-2 whitespace-nowrap ${
                        activeSection === section.id
                          ? "bg-blue-100 dark:bg-gray-900 dark:text-white text-blue-700 font-medium"
                          : "text-gray-600 hover:bg-gray-200 dark:text-gray-200 dark:hover:bg-gray-900"
                      }`}
                    >
                      <span className="mr-2">{section.icon}</span>
                      {section.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6 h-20">
                {activeSection === "personal" && (
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-800 border-b pb-2 dark:border-gray-700 dark:text-white">
                      Personal Information
                    </h4>
                    <div className="grid grid-cols-1 gap-4">
                      <InfoItem
                        label="Full Name"
                        value={selectedEmployee?.full_name || "-"}
                      />
                      <InfoItem
                        label="Employee ID"
                        value={selectedEmployee?.employee_id || "-"}
                      />
                      <InfoItem
                        label="Gender"
                        value={selectedEmployee?.gender || "-"}
                      />
                      <InfoItem
                        label="Date of Birth"
                        value={selectedEmployee?.date_of_birth || "-"}
                      />
                      <InfoItem
                        label="Marital Status"
                        value={selectedEmployee?.marital_status || "-"}
                      />
                      <InfoItem
                        label="Marriage Date"
                        value={selectedEmployee?.marriage_date || "-"}
                      />
                    </div>
                  </div>
                )}

                {activeSection === "demographic" && (
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-800 border-b pb-2 dark:border-gray-700 dark:text-white">
                      Demographic Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <InfoItem
                        label="Nationality"
                        value={selectedEmployee?.nationality || "-"}
                      />
                      <InfoItem
                        label="Religion"
                        value={selectedEmployee?.religion || "-"}
                      />
                      <InfoItem
                        label="Caste"
                        value={selectedEmployee?.caste || "-"}
                      />
                      <InfoItem
                        label="Sub Caste"
                        value={selectedEmployee?.sub_caste || "-"}
                      />
                    </div>
                  </div>
                )}

                {activeSection === "address" && (
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-800 border-b dark:border-gray-700 pb-2 dark:text-white">
                      Address
                    </h4>
                    <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                      <p className="text-gray-700 whitespace-pre-line dark:text-gray-200">
                        {selectedEmployee?.address || "-"}
                      </p>
                    </div>
                  </div>
                )}

                {activeSection === "physical" && (
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-800 border-b  dark:border-gray-700 pb-2 dark:text-white">
                      Physical Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <InfoItem
                        label="Blood Group"
                        value={selectedEmployee?.blood_group || "-"}
                      />
                      <InfoItem
                        label="Height"
                        value={`${selectedEmployee?.height} ft` || "-"}
                      />
                      <InfoItem
                        label="Weight"
                        value={`${selectedEmployee?.weight} kg` || "-"}
                      />
                    </div>

                    {selectedEmployee.is_handicapped && (
                      <>
                        <h4 className="font-medium text-gray-800 border-b pb-2 mt-6">
                          Disability Information
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <InfoItem
                            label="Handicap %"
                            value={selectedEmployee?.handicap_percentage || "-"}
                          />
                          <InfoItem
                            label="Nature"
                            value={selectedEmployee?.handicap_nature || "-"}
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}

                {activeSection === "family" && (
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-800 border-b dark:border-gray-700 pb-2 dark:text-white">
                      Family Information
                    </h4>
                    <div className="grid grid-cols-1 gap-4">
                      <InfoItem
                        label="Father's Name"
                        value={selectedEmployee?.father_name || "-"}
                      />
                      <InfoItem
                        label="Mother's Name"
                        value={selectedEmployee?.mother_name || "-"}
                      />
                      <InfoItem
                        label="Spouse's Name"
                        value={selectedEmployee?.spouse_name || "-"}
                      />
                    </div>
                  </div>
                )}

                {activeSection === "language" && (
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-800 border-b dark:border-gray-700 pb-2 dark:text-white">
                      Languages Known
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedEmployee?.languages
                        ?.split(",")
                        .map((lang, index) => (
                          <span
                            key={index}
                            className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm"
                          >
                            {lang.trim() || "-"}
                          </span>
                        ))}
                    </div>
                  </div>
                )}

                {activeSection === "documents" && (
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-800 border-b dark:border-gray-700 pb-2 dark:text-white">
                      Identification Documents
                    </h4>
                    <div className="grid grid-cols-1 gap-4">
                      <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                        <p className="text-sm font-medium text-gray-600 mb-1 dark:text-white">
                          Aadhar Card
                        </p>
                        <p className="text-gray-800 dark:text-gray-200">
                          {selectedEmployee?.aadhar_no || "-"}
                        </p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                        <p className="text-sm font-medium text-gray-600 mb-1 dark:text-white">
                          PAN Card
                        </p>
                        <p className="text-gray-800 dark:text-gray-200">
                          {selectedEmployee?.pan_no || "-"}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <p className="text-gray-500">No data to display</p>
          )}
        </Drawer>
      </div>
    </Layout>
  );
}
